/*
 * Kaeda Co motion system.
 *
 *   .apd-reveal          — fades/rises into view once
 *   .apd-reveal-child    — staggers inside a revealed parent
 *   [data-apd-parallax]  — drifts vertically as it crosses the viewport
 *   [data-apd-lines]     — reveals a heading line by line
 *
 * Content stays visible until JS opts into motion (html.apd-motion), so a
 * failed observer can never leave sections stuck at opacity 0.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealNodes = [...document.querySelectorAll('.apd-reveal')];

  const show = (node) => {
    if (!node || node.classList.contains('is-inview')) return;
    node.classList.add('is-inview');
    node.querySelectorAll('.apd-reveal-child').forEach((child, i) => {
      child.style.transitionDelay = `${120 + i * 90}ms`;
    });
  };

  const showAll = () => {
    for (const node of revealNodes) show(node);
  };

  const revealVisible = () => {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    for (const node of revealNodes) {
      const rect = node.getBoundingClientRect();
      if (rect.top < vh * 0.95 && rect.bottom > 40) show(node);
    }
  };

  /* Enable hide-until-reveal only after JS is ready */
  if (revealNodes.length && !reduce.matches) {
    document.documentElement.classList.add('apd-motion');
  }

  if (revealNodes.length) {
    if (reduce.matches || !('IntersectionObserver' in window)) {
      showAll();
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            show(entry.target);
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px 15% 0px', threshold: 0.05 }
      );

      for (const node of revealNodes) observer.observe(node);

      // Catch anything already on screen (and retry after layout/fonts).
      revealVisible();
      window.requestAnimationFrame(revealVisible);
      window.setTimeout(revealVisible, 200);
      window.setTimeout(revealVisible, 800);

      // Hard safety — never leave content invisible.
      window.setTimeout(showAll, 3500);

      window.addEventListener('scroll', revealVisible, { passive: true });
      window.addEventListener('resize', revealVisible, { passive: true });
    }
  }

  /* ------------------------------------------------------- line-by-line type */

  const lineNodes = document.querySelectorAll('[data-apd-lines]');

  if (lineNodes.length && !reduce.matches) {
    for (const node of lineNodes) {
      const text = node.textContent.trim();
      if (!text || node.children.length) continue;

      const words = text.split(/\s+/);
      node.textContent = '';

      const probes = words.map((word) => {
        const span = document.createElement('span');
        span.textContent = word;
        span.style.display = 'inline-block';
        node.append(span, document.createTextNode(' '));
        return span;
      });

      const lines = [];
      let current = null;
      let lastTop = null;

      for (const [i, probe] of probes.entries()) {
        const top = probe.offsetTop;
        if (lastTop === null || Math.abs(top - lastTop) > 2) {
          current = [];
          lines.push(current);
          lastTop = top;
        }
        current.push(words[i]);
      }

      node.textContent = '';

      for (const [i, line] of lines.entries()) {
        const mask = document.createElement('span');
        mask.className = 'apd-line';

        const inner = document.createElement('span');
        inner.className = 'apd-line__inner';
        inner.textContent = line.join(' ') + (i < lines.length - 1 ? ' ' : '');
        inner.style.transitionDelay = `${i * 110}ms`;

        mask.append(inner);
        node.append(mask);
      }
    }
  }

  /* -------------------------------------------------------------- parallax */

  const parallaxNodes = [...document.querySelectorAll('[data-apd-parallax]')];

  if (parallaxNodes.length && !reduce.matches) {
    let ticking = false;

    const update = () => {
      const viewport = window.innerHeight;

      for (const node of parallaxNodes) {
        const rect = node.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewport) continue;

        const progress = (rect.top + rect.height / 2 - viewport / 2) / viewport;
        const strength = Number(node.dataset.apdParallax) || 12;

        node.style.setProperty('--apd-shift', `${(progress * strength).toFixed(2)}%`);
      }

      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  reduce.addEventListener('change', (event) => {
    if (!event.matches) return;
    document.documentElement.classList.remove('apd-motion');
    showAll();
    for (const node of parallaxNodes) node.style.setProperty('--apd-shift', '0%');
  });
})();
